// The Mission Panel card as pure data (#6785, epic #6552; prototype `window.MissionDeck`,
// `docs/design/fleet-redesign/prototype.html`): the card's state and its reason, the
// blocker join, every zone's contents, the "Running hot" pick, the "..." menu by role and
// org, and the simplified card's four rows. `components/machine-card/` renders it; nothing
// here reads the url or a signal, and the clock is the caller's `nowMs`.
//
// EVERY JUDGEMENT IS THE SERVER'S, WITH ONE SPECIFIED EXCEPTION. The state reads
// `verdict.status` (#6557) and the bars `resourceBar` (`./machine-bars`); nothing here
// re-grades a raw reading into a tier. The exception is "Running hot" ({@link runningHot}):
// #6785 itself specifies it as a client pick off the server's CPU ratio, shown at 85% or
// more and naming the machine with the most room under 70% (`HOT_AT`, `ROOM_UNDER`). It is a
// hint, never a state or a tone, and no server field carries it.
//
// A BLOCKER BELONGS TO A CARD ONLY THROUGH ITS JOBS. Ruling V9 item 5: the card's state is
// the worst of the machine's jobs and its verdict, so only a blocker whose `task_id` is one
// of the machine's jobs (`jobs[]`, #6745) drives Blocked or Needs you, adds a row, a tag
// chip or a count. `blockers.runner_id` is the warm-tree hint from a run that has already ended
// (`packages/domain/src/entities/blocker.ts`, "THE WARM BOX IS A HINT"): where the work
// would like to resume, not what the machine is doing, so it is never read here. A machine
// from another org (`via_org`, G23) never shows a blocker: the read is this org's.
//
// A ROLE IS NAMED ONLY WHERE THE CLASS IDENTIFIES THE AGENT. `current_class` is the job's
// SCHEDULING class, and `coding` is every producer's default (`taskClassOf`), so it covers
// CI-fix, DevOps and issue work alike; only `gardener` has one agent. The chips come from
// task origins, so the one a class can light is Gardener (`kb_fold`, on a machine offered
// model calls since #6760).
//
// EPICS COME FROM THE JOBS (#6745, `cardEpicsFromJobs`). `epics: null` means not reported,
// and then the card claims nothing about them: no epic rows, no "No epic assigned", no "0 epics".

import {
  AGENT_ROSTER,
  AgentId,
  type BlockerKind,
  effectiveProvisions,
  isModelCallOrigin,
  type RunnerRole,
  requiredCapabilitiesForOrigin,
  runnerMayAnswerModelCalls,
  runnerRoleCanRunOrigin,
  type TaskLlmPreference,
  TaskOrigin,
} from '@developerz/domain';
import type { Translator } from '@developerz/i18n';
import { AGENT_NAME_KEY } from './agents';
import type { ApiBlocker, BlockerAnswered } from './blockers';
import type { CardMenuId, CardMenuItem, CardMenuOptions } from './card-menu-types';
import { drainAction } from './fleet-machine-panel';
import { OFFLINE_STATUSES, shortId } from './fleet-machines';
import type { UtilBox } from './fleetUtilization';
import { type BarTone, RESOURCE_KINDS, type ResourceBar, resourceBar } from './machine-bars';
import { boxJobs, runningJobs, zoneCount } from './machine-jobs';
import {
  type HealthTone,
  MACHINE_HEALTH,
  MACHINE_STATUS,
  serverText,
  taskLine,
} from './machine-labels';
import { moveWorkItems } from './move-menu-items';
import { ago, clock, clockOrDay } from './time';

/** The caller's translator: the card picks plurals through its `plural` (`tr.plural`). */
type Tr = Translator;

/** The card's state, first match wins (see {@link cardState}). The last three are v1's quiet words. */
export type CardState =
  | 'offline'
  | 'blocked'
  | 'needs'
  | 'paused'
  | 'autonomous'
  | 'idle'
  | 'starting'
  | 'waiting'
  | 'unknown';

/** `live` is the accent; `idle` a quiet grey; healthy is never green. */
export type CardTone = 'live' | 'crit' | 'warn' | 'neutral' | 'idle';

type StateSpec = {
  readonly label: string;
  readonly tone: CardTone;
  /** A loud state shows the tinted banner and hides the header chip in Full. */
  readonly loud: boolean;
  /** The action button's words for an admin on an own-org machine; null shows none. */
  readonly action: string | null;
};

/** The prototype's `ST` table (`:1340-1347`), with today's quiet v1 states beside it. */
export const CARD_STATES: Readonly<Record<CardState, StateSpec>> = {
  offline: {
    label: MACHINE_STATUS.offline,
    tone: 'crit',
    loud: true,
    action: 'fleet.machines.card_action_reconnect',
  },
  blocked: {
    label: 'fleet.machines.card_state_blocked',
    tone: 'crit',
    loud: true,
    action: 'fleet.machines.card_action_fix',
  },
  needs: {
    label: 'fleet.machines.card_state_needs',
    tone: 'warn',
    loud: true,
    action: 'fleet.machines.card_action_decide',
  },
  paused: {
    label: MACHINE_STATUS.paused,
    tone: 'neutral',
    loud: true,
    action: 'fleet.machines.card_action_resume',
  },
  autonomous: {
    label: 'fleet.machines.card_state_autonomous',
    tone: 'live',
    loud: false,
    action: 'fleet.machines.card_action_open',
  },
  idle: {
    label: MACHINE_STATUS.idle,
    tone: 'idle',
    loud: false,
    action: 'fleet.machines.card_action_assign',
  },
  starting: {
    label: MACHINE_STATUS.starting,
    tone: 'idle',
    loud: false,
    action: 'fleet.machines.card_action_open',
  },
  waiting: {
    label: MACHINE_STATUS.waiting_to_connect,
    tone: 'idle',
    loud: false,
    action: 'fleet.machines.card_action_open',
  },
  unknown: {
    label: MACHINE_STATUS.unknown,
    tone: 'idle',
    loud: false,
    action: 'fleet.machines.card_action_open',
  },
};

/** A blocker's tag on the card: Blocked (something is broken) or Parked (a person decides). */
export type BlockerTag = 'blocked' | 'parked';

export const BLOCKER_TAG: Readonly<Record<BlockerKind, BlockerTag>> = {
  missing_credential: 'blocked',
  unreachable_service: 'blocked',
  decision_needed: 'parked',
};

export const BLOCKER_TAG_LABEL: Readonly<Record<BlockerTag, string>> = {
  blocked: 'fleet.machines.card_state_blocked',
  parked: 'fleet.machines.card_tag_parked',
};

/** One blocker row: tag, id, summary, repo; a Parked one also asks its question. */
export type CardBlocker = {
  readonly id: string;
  readonly tag: BlockerTag;
  readonly summary: string;
  readonly question: string | null;
  readonly repo: string | null;
  /**
   * "Answer here" (#6726): a Parked question asked in prose (no `options` pick, #6387, no
   * `needs` form), for an admin (`canAdminWrite`, owner ruling 2026-10-21). Picks and forms,
   * and every member and viewer, answer on the Blockers page, which renders them.
   */
  readonly answerHere: boolean;
};

/** The card's own answer limit (#6726); the server's bound is `BLOCKER_ANSWER_MAX`, 4,000. */
export const ANSWER_MAX = 600;
const ANSWER_MIN = 2;
/** How long the answer's note stays on the card (#6726, the prototype's `until`). */
export const ANSWER_NOTE_MS = 6_000;

/** "Send answer" is enabled at two or more characters once trimmed. */
export const canSendAnswer = (text: string): boolean => text.trim().length >= ANSWER_MIN;

const ANSWER_NOTE: Readonly<Record<BlockerAnswered['arm'], string>> = {
  triage: 'fleet.machines.card_answer_triage',
  released: 'fleet.machines.card_answer_released',
  linked: 'fleet.machines.card_answer_linked',
};

/**
 * The note an answer leaves on the card, by what it really did. Only a released or linked
 * answer made work carry on; the triage arm filed an ask and resumed nothing, so it never
 * says "back in line". The subject is "The work": no field joins a blocker to an epic
 * number yet (`CardEpic` carries none), so the prototype's "Epic #<n>" is never claimed.
 */
export const answerNote = (done: BlockerAnswered, tr: Tr): string => tr(ANSWER_NOTE[done.arm]);

/**
 * One epic as #6745 reports it on a job (`{ plan_id, title, done, total, running }`), with
 * #6753's `paused_at` and `llm` when the api reports them (absent claims nothing).
 */
export type CardEpic = {
  readonly plan_id: string;
  readonly title: string;
  readonly done: number;
  readonly total: number;
  readonly running: number;
  readonly paused_at?: string | null;
  readonly llm?: TaskLlmPreference | null;
};

export type CardContext = {
  /** The whole fleet read: "Running hot" names the own-org machine with the most room. */
  readonly boxes: readonly UtilBox[];
  /** This org's open blockers by task id ({@link blockersByTask}). */
  readonly byTask: ReadonlyMap<string, readonly ApiBlocker[]>;
  /** `canAdminWrite` on a settled role (owner ruling G21). */
  readonly canWrite: boolean;
  readonly nowMs: number;
  /** The jobs' epics (`cardEpicsFromJobs`), or `null` when not reported. */
  readonly epics: readonly CardEpic[] | null;
};

/** The open blockers by the task they hold up; one without a task joins no card. */
export function blockersByTask(
  blockers: readonly ApiBlocker[],
): ReadonlyMap<string, readonly ApiBlocker[]> {
  const out = new Map<string, ApiBlocker[]>();
  for (const b of blockers) {
    if (b.task_id === null) continue;
    const list = out.get(b.task_id);
    if (list === undefined) out.set(b.task_id, [b]);
    else list.push(b);
  }
  return out;
}

/**
 * The blockers on this card: those on ANY of its jobs, none on another org's machine.
 * `canWrite` is `canAdminWrite`: it decides "Answer here" and nothing else.
 */
export function cardBlockers(
  box: UtilBox,
  byTask: ReadonlyMap<string, readonly ApiBlocker[]>,
  canWrite: boolean,
): readonly CardBlocker[] {
  if (box.via_org !== null) return [];
  return boxJobs(box)
    .flatMap((job) => byTask.get(job.id) ?? [])
    .map((b) => {
      const tag = BLOCKER_TAG[b.kind];
      return {
        id: b.id,
        tag,
        summary: b.summary,
        question: tag === 'parked' ? b.detail : null,
        repo: b.repo_full_name,
        answerHere: canWrite && tag === 'parked' && b.options === null && b.needs === null,
      };
    });
}

const QUIET: Readonly<Record<UtilBox['verdict']['status'], CardState>> = {
  working: 'autonomous',
  idle: 'idle',
  paused: 'paused',
  starting: 'starting',
  waiting_to_connect: 'waiting',
  unknown: 'unknown',
  not_responding: 'offline',
  offline: 'offline',
};

/**
 * Offline > Blocked > Needs you > Paused > Autonomous > Idle, then the quiet v1 states.
 * Offline is the server's verdict only: a drained machine that went dark has verdict
 * `paused` (`machineStatus` tests the drain first), so it reads Paused.
 */
export function cardState(box: UtilBox, blockers: readonly CardBlocker[]): CardState {
  if (OFFLINE_STATUSES.includes(box.verdict.status)) return 'offline';
  if (blockers.some((b) => b.tag === 'blocked')) return 'blocked';
  if (blockers.some((b) => b.tag === 'parked')) return 'needs';
  return QUIET[box.verdict.status];
}

/** The agent the running job's class identifies: only `gardener` has one agent. */
export function cardRole(box: UtilBox): AgentId | null {
  return box.current_class === 'gardener' ? AgentId.Gardener : null;
}

/**
 * The roles today's claim can give THIS machine (#6765): the agent of every task origin
 * `runnerRoleCanRunOrigin` admits for `mode`, dropping origins that run no model of their
 * own and every origin whose required capabilities the box's REPORTED build does not
 * derive. Every token `requiredCapabilitiesForOrigin` names is platform-derived from the
 * version (`effectiveProvisions`), so `null` capabilities suffice: a `0.1.22` box shows
 * Triage and no Planner. A model-call origin also needs the claim's own machine gate,
 * `runnerMayAnswerModelCalls` (not donated, org-owned; with the build, the claim's own
 * `runnerServesModelCalls`). A row whose `earmark` did not arrive (an api older than the
 * field) is not known to qualify, so it shows no model-call chip.
 */
export function capabilityChips(
  mode: RunnerRole,
  box: Pick<UtilBox, 'earmark' | 'owner_kind' | 'runner_version'>,
): readonly AgentId[] {
  const provisions = effectiveProvisions(null, box.runner_version);
  const answersModelCalls =
    box.earmark !== undefined &&
    runnerMayAnswerModelCalls({ earmark: box.earmark, ownerKind: box.owner_kind });
  const out: AgentId[] = [];
  for (const origin of Object.values(TaskOrigin)) {
    const binding = AGENT_ROSTER.origin[origin];
    if (typeof binding !== 'string' || !runnerRoleCanRunOrigin(mode, origin)) continue;
    if (!requiredCapabilitiesForOrigin(origin).every((t) => provisions.includes(t))) continue;
    if (isModelCallOrigin(origin) && !answersModelCalls) continue;
    if (!out.includes(binding)) out.push(binding);
  }
  return out;
}

const HOT_AT = 0.85;
const ROOM_UNDER = 0.7;

/**
 * "Running hot" at 85% CPU or more: the own-org machine, working or idle, with the lowest
 * CPU among those under 70%, else nothing (the prototype took the first under 70%, V9 item 6).
 */
export function runningHot(box: UtilBox, boxes: readonly UtilBox[]): string | null {
  const ratio = box.verdict.signals.cpu.ratio;
  if (ratio === null || ratio < HOT_AT) return null;
  let best: { name: string; ratio: number } | null = null;
  for (const o of boxes) {
    const r = o.verdict.signals.cpu.ratio;
    if (o.id === box.id || o.via_org !== null || r === null || r >= ROOM_UNDER) continue;
    if (o.verdict.status !== 'working' && o.verdict.status !== 'idle') continue;
    if (best === null || r < best.ratio) best = { name: o.display_name, ratio: r };
  }
  return best?.name ?? null;
}

// ─── The menu ────────────────────────────────────────────────────────────────

export type { CardMenuId, CardMenuItem, CardMenuOptions } from './card-menu-types';

/** "Pause all jobs" / "Resume all jobs" (#6753, brief menu group 2), with the job count. */
function jobsItem(jobs: NonNullable<CardMenuOptions['jobs']>): CardMenuItem {
  const extra = { detail: String(jobs.count), ...(jobs.locked ? { disabled: true as const } : {}) };
  return jobs.value === 'run'
    ? item(
        'pause_jobs',
        'fleet.machines.card_menu_pause_jobs',
        'fleet.machines.card_menu_pause_jobs_title',
        extra,
      )
    : item(
        'resume_jobs',
        'fleet.machines.card_menu_resume_jobs',
        'fleet.machines.card_menu_resume_jobs_title',
        extra,
      );
}

const item = (
  id: CardMenuId,
  label: string,
  title: string,
  extra: Pick<CardMenuItem, 'detail' | 'danger' | 'disabled' | 'detailKey'> = {},
): CardMenuItem => ({ id, label, title, ...extra });

/**
 * Send work (#6770, prototype `:1862`): the card's own group after Machine details, for an
 * admin on an own-org machine and only on the CARD (a List row has no Now zone for the form).
 * On an offline machine it stays, `aria-disabled`, reading "Offline".
 */
function sendItem(box: UtilBox): CardMenuItem {
  return OFFLINE_STATUSES.includes(box.verdict.status)
    ? item('send', 'fleet.send.label', 'fleet.send.offline_title', {
        disabled: true,
        detailKey: 'fleet.send.menu_offline',
      })
    : item('send', 'fleet.send.label', 'fleet.send.menu_title');
}

/**
 * The "..." menu (prototype cmenuHTML, `:1857-1884`) in its groups. Members, viewers and
 * another org's machine get the read items only (G21, G23); an admin on an own-org card gets
 * every item: Send work after Machine details (#6770), Move work and Drain machine (#6772),
 * Pause all / Resume all jobs (#6753). "Jobs at once" (#6740, a v2 addition) sits between
 * Rename and Instructions, its detail the STORED cap (`max_slots`), opening `MachineJobsAtOnce`.
 */
export function cardMenu(box: UtilBox, o: CardMenuOptions): readonly (readonly CardMenuItem[])[] {
  const write = o.canWrite && box.via_org === null;
  const drain = write ? drainAction(box) : null;
  const groups: CardMenuItem[][] = [
    [item('details', 'fleet.machines.menu_details', 'fleet.machines.menu_details_title')],
    write && o.variant === 'card' ? [sendItem(box)] : [],
    write && o.variant === 'card' && o.move != null ? [...moveWorkItems(box, o.move)] : [],
    write && o.variant === 'card' && o.jobs != null ? [jobsItem(o.jobs)] : [],
    drain === null
      ? []
      : [
          drain.resume
            ? item('undrain', drain.label, 'fleet.machines.card_menu_resume_title')
            : item('drain', drain.label, 'fleet.machines.card_menu_pause_title'),
        ],
    [
      ...(o.variant === 'card'
        ? [
            o.simplified
              ? item(
                  'expand',
                  'fleet.machines.card_menu_expand',
                  'fleet.machines.card_menu_expand_title',
                )
              : item(
                  'simplify',
                  'fleet.machines.card_menu_simplify',
                  'fleet.machines.card_menu_simplify_title',
                ),
          ]
        : []),
      item('agents', 'fleet.machines.card_menu_agents', 'fleet.machines.card_menu_agents_title', {
        detail: '↗',
      }),
      item('copy_id', 'fleet.machines.card_menu_copy', 'fleet.machines.card_menu_copy_title', {
        detail: shortId(box.id),
      }),
    ],
    write
      ? [
          item('rename', 'fleet.machines.menu_rename', 'fleet.machines.menu_rename_title'),
          item('jobs_at_once', 'fleet.jobs_at_once.label', 'fleet.jobs_at_once.menu_title', {
            detail: String(box.max_slots),
          }),
          item(
            'instructions',
            'fleet.machines.card_menu_instructions',
            'fleet.machines.card_menu_instructions_title',
          ),
        ]
      : [],
    write
      ? [
          item('remove', 'fleet.machines.menu_remove', 'fleet.machines.card_menu_remove_title', {
            danger: true,
          }),
        ]
      : [],
  ];
  return groups.filter((g) => g.length > 0);
}

// ─── The view ────────────────────────────────────────────────────────────────

export type CardEpicRow = {
  readonly plan_id: string;
  readonly title: string;
  /** "3/7". */
  readonly count: string;
  readonly done: number;
  readonly running: number;
  readonly total: number;
  readonly status: string;
  /** "All slices done" is the one green status word; "Waiting on you" is amber. */
  readonly tone: 'settled' | 'warn' | null;
  /** The epic's pause mark and stored model (#6753), when reported: the controls read them. */
  readonly paused_at?: string | null;
  readonly llm?: TaskLlmPreference | null;
};

export type MissionView = {
  readonly epics: readonly CardEpicRow[];
  /** Epics reported and none of them: the prototype's "No epic assigned". */
  readonly noEpic: boolean;
  /** "N epics", with two or more. */
  readonly epicCount: string | null;
  /** "1 needs a human" / "N need a human": the blocked and parked rows. */
  readonly needsHuman: string | null;
  readonly blockers: readonly CardBlocker[];
};

export type MiniBar = {
  readonly label: string;
  readonly value: string;
  readonly fill: number;
  readonly tone: BarTone;
};

export type SimplifiedView = {
  readonly status: {
    readonly loud: boolean;
    readonly tone: CardTone;
    readonly reason: string;
    /** What the status region announces: {@link CardView.liveReason}. */
    readonly liveReason: string;
    readonly action: string | null;
  };
  /** The roles running, or the job's own line. */
  readonly what: string;
  /** "1 job", "No jobs", or "1 job · 2 epics" once epics are reported. */
  readonly count: string;
  /** The one combined slice bar; null while epics are not reported (an empty track, "-"). */
  readonly slices: {
    readonly done: number;
    readonly running: number;
    readonly total: number;
    readonly text: string;
  } | null;
  /** CPU, Mem, Disk; null when offline ("Resources unknown"). */
  readonly res: readonly MiniBar[] | null;
};

export type CardView = {
  readonly state: CardState;
  readonly label: string;
  readonly tone: CardTone;
  readonly loud: boolean;
  readonly reason: string;
  /**
   * The reason as a loud state's status region announces it: the same words, except that
   * Offline drops its ticking "Last seen <ago>", which would re-announce on every poll (the
   * footer still says when it was last seen).
   */
  readonly liveReason: string;
  /** The action button's words; "Open" for a read-only card; null shows none. */
  readonly action: string | null;
  /** A member, a viewer or another org's machine: no write anywhere on the card. */
  readonly readOnly: boolean;
  readonly identity: {
    readonly id: string;
    readonly server: string | null;
    readonly health: string;
    readonly healthTone: HealthTone;
  };
  readonly fromOrg: string | null;
  readonly resources: {
    readonly side: string;
    /** Null when offline: "Unknown while offline". */
    readonly bars: readonly ResourceBar[] | null;
    readonly hot: string | null;
  };
  readonly now: {
    /** The zone header's count (`zoneCount`): "Coder", "N jobs", or null with no job. */
    readonly count: string | null;
    /** The status sentence a machine with no job shows (`taskLine`). */
    readonly status: string;
    readonly tag: BlockerTag | null;
    readonly hasJob: boolean;
  };
  /** Null when there is nothing to say: no epics reported and no blocker on its task. */
  readonly mission: MissionView | null;
  readonly capabilities: {
    readonly chips: readonly {
      readonly id: AgentId;
      readonly name: string;
      readonly lit: boolean;
    }[];
    readonly running: string | null;
  };
  readonly footer: { readonly reason: string | null; readonly seen: string };
  readonly simplified: SimplifiedView;
};

function stateReason(
  box: UtilBox,
  state: CardState,
  blockers: readonly CardBlocker[],
  tr: Tr,
  nowMs: number,
): string {
  switch (state) {
    case 'offline':
      return box.last_beat_iso === null
        ? tr('fleet.machines.card_reason_offline_never')
        : tr('fleet.machines.card_reason_offline', { ago: ago(box.last_beat_iso, nowMs) });
    case 'blocked':
    case 'needs': {
      const tag: BlockerTag = state === 'blocked' ? 'blocked' : 'parked';
      return blockers.find((b) => b.tag === tag)?.summary ?? '';
    }
    case 'paused': {
      const at = box.drained_iso ?? null;
      return at === null
        ? tr('fleet.machines.card_reason_paused')
        : tr('fleet.machines.card_reason_paused_at', { time: clockOrDay(at) });
    }
    case 'autonomous': {
      const at = runningJobs(box)[0]?.started_at ?? null;
      return at === null
        ? tr('fleet.machines.card_reason_working')
        : tr('fleet.machines.card_reason_working_since', { time: clock(at) });
    }
    case 'idle':
      return tr('fleet.machines.card_reason_idle');
    default:
      return taskLine(box, tr);
  }
}

/**
 * An epic's status word, the prototype's progressHTML order (`:2353-2362`): "Paused by you"
 * when the epic's own `paused_at` is set (#6753), ahead of every other word; "Waiting on
 * you" on the first epic of a card that needs a person, "<n> running", "All slices done",
 * "Planned, not started" on a card that needs a person before the epic's first slice, else
 * "Queued". The prototype's "Stopped" reads a stopped-slice count nobody serves and is not
 * built (#6753 deviation 8): a stopped epic's slices are cancelled and leave the card.
 */
function epicStatus(
  e: CardEpic,
  at: { readonly first: boolean; readonly needs: boolean },
  tr: Tr,
): { readonly status: string; readonly tone: 'settled' | 'warn' | null } {
  if (e.paused_at != null) return { status: tr('fleet.machines.card_epic_paused'), tone: null };
  if (at.needs && at.first) return { status: tr('fleet.machines.card_epic_waiting'), tone: 'warn' };
  if (e.running > 0) {
    return { status: tr('fleet.machines.card_epic_running', { count: e.running }), tone: null };
  }
  if (e.total > 0 && e.done >= e.total) {
    return { status: tr('fleet.machines.card_epic_done'), tone: 'settled' };
  }
  if (at.needs && e.done === 0)
    return { status: tr('fleet.machines.card_epic_planned'), tone: null };
  return { status: tr('fleet.machines.card_epic_queued'), tone: null };
}

function epicRow(
  e: CardEpic,
  at: { readonly first: boolean; readonly needs: boolean },
  tr: Tr,
): CardEpicRow {
  const { status, tone } = epicStatus(e, at, tr);
  return {
    plan_id: e.plan_id,
    title: e.title,
    count: `${e.done}/${e.total}`,
    done: e.done,
    running: e.running,
    total: e.total,
    status,
    tone,
    ...(e.paused_at === undefined ? {} : { paused_at: e.paused_at }),
    ...(e.llm === undefined ? {} : { llm: e.llm }),
  };
}

function missionView(
  epics: readonly CardEpic[] | null,
  blockers: readonly CardBlocker[],
  needs: boolean,
  tr: Tr,
): MissionView | null {
  if (epics === null && blockers.length === 0) return null;
  const rows = (epics ?? []).map((e, i) => epicRow(e, { first: i === 0, needs }, tr));
  return {
    epics: rows,
    noEpic: epics !== null && epics.length === 0 && blockers.length === 0,
    epicCount:
      rows.length >= 2
        ? tr.plural('fleet.machines.card_epics', rows.length, { count: rows.length })
        : null,
    needsHuman:
      blockers.length === 0
        ? null
        : tr.plural('fleet.machines.card_needs_human', blockers.length, {
            count: blockers.length,
          }),
    blockers,
  };
}

const MINI_LABEL: Readonly<Record<ResourceBar['kind'], string>> = {
  cpu: 'fleet.machines.card_mini_cpu',
  memory: 'fleet.machines.card_mini_mem',
  disk: 'fleet.machines.card_mini_disk',
};

function simplifiedView(
  box: UtilBox,
  parts: {
    readonly spec: StateSpec;
    readonly reason: string;
    readonly liveReason: string;
    readonly action: string | null;
    readonly role: string | null;
    readonly bars: readonly ResourceBar[] | null;
    readonly epics: readonly CardEpic[] | null;
    readonly offline: boolean;
  },
  tr: Tr,
): SimplifiedView {
  const jobs = boxJobs(box).length;
  const jobText =
    jobs === 0
      ? tr('fleet.machines.card_no_jobs')
      : tr.plural('fleet.machines.card_jobs', jobs, { count: jobs });
  const e = parts.epics;
  const what =
    parts.role === null
      ? taskLine(box, tr)
      : parts.offline
        ? tr('fleet.machines.card_last_known', { roles: parts.role })
        : parts.role;
  let slices: SimplifiedView['slices'] = null;
  if (e !== null && e.length > 0) {
    const done = e.reduce((s, x) => s + x.done, 0);
    const running = e.reduce((s, x) => s + x.running, 0);
    const total = e.reduce((s, x) => s + x.total, 0);
    slices = { done, running, total, text: `${done}/${total}` };
  }
  return {
    status: {
      loud: parts.spec.loud,
      tone: parts.spec.tone,
      reason: parts.reason,
      liveReason: parts.liveReason,
      action: parts.action,
    },
    what,
    count:
      e === null
        ? jobText
        : `${jobText} · ${tr.plural('fleet.machines.card_epics', e.length, { count: e.length })}`,
    slices,
    res:
      parts.bars === null
        ? null
        : parts.bars.map((b) => ({
            label: tr(MINI_LABEL[b.kind]),
            value: b.value,
            fill: b.fill,
            tone: b.tone,
          })),
  };
}

function nowZone(box: UtilBox, blockers: readonly CardBlocker[], tr: Tr): CardView['now'] {
  const tag: BlockerTag | null = blockers.some((b) => b.tag === 'blocked')
    ? 'blocked'
    : blockers.length > 0
      ? 'parked'
      : null;
  return {
    count: zoneCount(box, tr),
    status: taskLine(box, tr),
    tag,
    hasJob: boxJobs(box).length > 0,
  };
}

/** Everything one card shows, through the caller's translator. */
export function cardView(box: UtilBox, c: CardContext, tr: Tr): CardView {
  const blockers = cardBlockers(box, c.byTask, c.canWrite);
  const state = cardState(box, blockers);
  const spec = CARD_STATES[state];
  const readOnly = !c.canWrite || box.via_org !== null;
  const offline = state === 'offline';
  const reason = stateReason(box, state, blockers, tr, c.nowMs);
  const liveReason = offline ? tr('fleet.machines.card_reason_offline_never') : reason;
  const action =
    spec.action === null ? null : tr(readOnly ? 'fleet.machines.card_action_open' : spec.action);
  const health = MACHINE_HEALTH[box.verdict.health];
  const roleId = cardRole(box);
  const role = roleId === null ? null : tr(AGENT_NAME_KEY[roleId]);
  const bars = offline ? null : RESOURCE_KINDS.map((k) => resourceBar(box, k, tr));
  const cores = box.specs?.cpu_cores ?? null;
  const seen =
    box.last_beat_iso === null
      ? tr('fleet.machines.card_seen_never')
      : tr('fleet.machines.card_seen', { ago: ago(box.last_beat_iso, c.nowMs) });
  return {
    state,
    label: tr(spec.label),
    tone: spec.tone,
    loud: spec.loud,
    reason,
    liveReason,
    action,
    readOnly,
    identity: {
      id: shortId(box.id),
      server: serverText(box),
      health: tr(health.key),
      healthTone: health.tone,
    },
    fromOrg: box.via_org,
    resources: {
      side:
        cores === null
          ? tr('fleet.machines.card_whole')
          : tr('fleet.machines.card_whole_cores', { cores }),
      bars,
      hot: offline ? null : runningHot(box, c.boxes),
    },
    now: nowZone(box, blockers, tr),
    mission: missionView(c.epics, blockers, state === 'needs', tr),
    capabilities: {
      chips: capabilityChips(box.mode, box).map((id) => ({
        id,
        name: tr(AGENT_NAME_KEY[id]),
        lit: roleId !== null && id === roleId,
      })),
      running: runningJobs(box).length === 0 ? null : tr('fleet.machines.card_caps_running'),
    },
    footer: { reason: spec.loud ? null : reason, seen },
    simplified: simplifiedView(
      box,
      { spec, reason, liveReason, action, role, bars, epics: c.epics, offline },
      tr,
    ),
  };
}
